// Filename: datascene.js
// Timestamp: 2018.12.10-09:28:51 (last modified)
// Author(s): bumblehead <chris@bumblehead.com>
//
// “scene”; a data container referencing sequences
//
// a client composes the sequences used to render each scene. sequences are
// defined with a 'name', a 'type' and a 'start' value (in seconds). depending
// upon 'type', other named-properties are anticipated.
//
// here, sequences compose a 'layers' node hierarchy and an interval tree.
// the sequential position of each sequence is preserved through edges
// connecting nodes via start time.
//
//                            tree        edge
//                        |           |
//   * scene              |   0       |
//     * marker-bgn       |   0-0     |   0-0
//     * layer            |   0-1     |   0-1-0
//       * shape          |   0-1-0   |   0-1-1
//       * shape          |   0-1-1   |   0-2
//     * marker-end       |   0-2     |
//   * scene              |   1       |
//     * marker-bgn       |   1-0     |   0-0
//     * layer            |   1-1     |   1-1-0
//       * shape          |   1-1-0   |   1-2-0
//     * layer            |   1-2     |   1-3
//       * shape          |   1-2-0   |
//     * marker-end       |   1-3     |
//   * scene              |   2       |
//     * marker-bgn       |   2-0     |   2-0
//     * layer            |   2-1     |   2-1-0
//       * shape          |   2-1-0   |   2-1-1
//       * shape          |   2-1-1   |   2-2
//     * marker-end       |   2-2     |
//                        |           |

import zagtree from 'zagtree'
import ptrecls from '@bumblehead/gani/src/parts/ptrecls.js'

import {
  gngraphNodeEdgeSet,
  gngraphNodeEdgeOutSet,
  gngraphNodeSet as graphNodeSet
} from '@bumblehead/gani/src/gngraph.js'

import {
  gnnodeIsSame as nodeIsSame,
  gnnodeNSTargetGetBySenderProp
} from '@bumblehead/gani/src/gnnode.js'

import {
  gnnodeosxGet as nodeosxGet
} from '@bumblehead/gani/src/gnnodeos.js'

import {
  gnedgeMetaTargetKeyGet
} from '@bumblehead/gani/src/gnedge.js'

import {
  gnaccSubj as accSubj,
  gnaccInit as accInit,
  gnaccBaseElem as accBaseElem
} from '@bumblehead/gani/src/gnacc.js'

import {
  gnconstKEY as KEY
} from '@bumblehead/gani/src/gnconst.js'

const DATASHAPESKEY = '/datashapes'

const xinit = (nd, ndx) => {
  if (typeof accInit.propget(nd, 'layersnum') !== 'number')
    nd = accInit.propset(nd, 'layersnum', 0)

  return ptrecls.xinit(nd, ndx)
}

const gngraph_followedge = (gr, ndsrc, edgename, fn, prefn) => {
  const ns = 'SUBJ'
  const senderprop = edgename
  const edge = gnnodeNSTargetGetBySenderProp(ndsrc, ns, senderprop)
  const edgetargetkey = edge && gnedgeMetaTargetKeyGet(edge)

  if (!edge) {
    return fn(null, gr, gr.get(ndsrc.get(KEY)))
  }

  prefn(gr, gr.get(edgetargetkey), edge, (e, gr, edgenode) => {
    if (e) return fn(e)

    gngraph_followedge(gr, edgenode, edgename, fn, prefn)
  })
}

const getscenemarkerbgnnode = (gr, ndscene) => (
  gr.get(`${ndscene.get(KEY)}/bgn`))

const getscenemarkerendnode = (gr, ndscene) => (
  gr.get(`${ndscene.get(KEY)}/end`))

const getscenelayernode = (gr, ndscene, layernum) => (
  gr.get(`${ndscene.get(KEY)}/layer-${layernum}`))

const postmarkerbgn = async (sess, cfg, gr, ndscene, pg) => (
  ptrecls.addchilddata(sess, cfg, gr, ndscene, pg, {
    name: 'bgn',
    type: 'marker',
    markertype: 'bgn',
    start: 0
  }))

const postmarkerend = async (ss, cfg, gr, ndscene, pg) => (
  ptrecls.addchilddata(ss, cfg, gr, ndscene, pg, {
    name: 'end',
    type: 'marker',
    markertype: 'end',
    start: accSubj.propget(ndscene, 'duration')
  }))

const postmarkers = async (ss, cfg, gr, ndscene, pg, fn) => {
  if (!ndscene) {
    throw new Error('invalid ndscene')
  }

  let res = await postmarkerbgn(ss, cfg, gr, ndscene, pg)
  gr = res[0]
  ndscene = res[1]
  let ndbgn = res[2]

  res = await postmarkerend(ss, cfg, gr, ndscene, pg)
  gr = res[0]
  ndscene = res[1]
  let ndend = res[2]

  const ns = 'SUBJ'

  res = gngraphNodeEdgeSet(gr,
    [ndbgn, ns, 'nextseq'],
    [ndend, ns, 'nextseq'])

  gr = res[0]
  const ndsender = res[1]
  const ndtarget = gr.get(ndend.get(KEY))

  fn(null, gr, ndscene, ndsender, ndtarget)
}

const scenelayersnumadded = (ndscene, layersnum) => {
  let scenelayersnum = Math.max(
    accInit.propget(ndscene, 'layersnum', 0), layersnum + 1)

  return accInit.propset(ndscene, 'layersnum', scenelayersnum)
}

const getpostlayer = async (ss, cfg, gr, ndscene, pg, layernum) => {
  const layernode = getscenelayernode(gr, ndscene, layernum)

  if (layernode) {
    return [gr, ndscene, layernode]
  }

  ndscene = scenelayersnumadded(ndscene, layernum)

  ;[gr, ndscene] = graphNodeSet(gr, ndscene)

  return ptrecls.addchilddata(ss, cfg, gr, ndscene, pg, {
    name: `layer-${layernum}`,
    type: 'layer'
  })
}

const getdatashapesnode = (ss, cfg, gr) => (
  gr.get(DATASHAPESKEY))

const setfragsequencedata = (ss, cfg, gr, frag, fn) => (
  fn(null, gr, getdatashapesnode(ss, cfg, gr)))

const addsequencenode = (ss, cfg, gr, ndscene, pg, pnode, frag, fn) => {
  if (!ndscene) {
    throw new Error('done')
  }

  let scenendend = getscenemarkerendnode(gr, ndscene)

  setfragsequencedata(ss, cfg, gr, frag, async (err, gr) => {
    if (err) return fn(err)

    let res = await ptrecls.addchilddata(ss, cfg, gr, pnode, pg, frag)
    gr = res[0]
    pnode = res[1]
    let seqnode = res[2]

    ;[gr] = gngraphNodeEdgeOutSet(gr,
      [seqnode, 'SUBJ', 'nextseq'],
      [scenendend, 'SUBJ', 'nextseq'])

    seqnode = gr.get(seqnode.get(KEY))
    fn(null, gr, ndscene, pnode, seqnode)
  })
}

const addlayerseq = async (ss, cfg, gr, ndscene, pg, seq, fn) => {
  const res = await getpostlayer(ss, cfg, gr, ndscene, pg, seq.col)
  gr = res[0]
  ndscene = res[1]
  let layernode = res[2]

  addsequencenode(ss, cfg, gr, ndscene, pg, layernode, seq, fn)
}

const getseqaugmented = async (ss, cfg, gr, nd, pg, seqarr = []) => {
  let tree = zagtree.newtree()

  // seqarr = seqarr.sort(o.sortseqtime)
  return new Promise((resolve, error) => {
    postmarkers(ss, cfg, gr, nd, pg, (err, gr, nd) => {
      if (err) return error(err)

      ;(function nextseq (seqarr, i, ilen, gr, ndscene) {
        if (i >= ilen) return resolve([gr, ndscene])

        let seq = seqarr[i]

        if (seq.type === 'camera') {
          // addcameraseq(ss, cfg, gr, ndscene, seq, (err, gr) => {
          //   if (err) return fn(err)
          //
          //   nextseq(seqarr, ++i, ilen, gr, ndscene)
          // })
        } else {
          if (!zagtree.istreenodeviable(tree, seq)) {
            tree = zagtree.newtree()
          }

          if (!zagtree.isnodevalid(seq)) {
            seq.start = 0
            seq.end = 30
          }

          [ tree, seq ] = zagtree.addperiod(tree, seq)

          addlayerseq(ss, cfg, gr, ndscene, pg, seq,
            async (err, gr, ndscene) => {
              if (err) return error(err)

              let res = await addseqrefdata(ss, cfg, gr, ndscene, pg, seq)
              gr = res[0]
              ndscene = res[1]

              nextseq(seqarr, ++i, ilen, gr, ndscene)
            }
          )
        }
      }(seqarr, 0, seqarr.length, gr, nd))
    })
  })
}

const get_best_format = ([ssnum]) => {
  let format = 'm:ss'

  if (ssnum >= 36000) { // 10 hours
    format = 'hh:mm:ss'
  } else if (ssnum >= 3600) { // lt 10 hours
    format = 'h:mm:ss'
  } else if (ssnum >= 600) { // 10 min
    format = 'mm:ss'
  } else if (ssnum >= 60) { // lt 10 min
    format = 'm:ss'
  }

  return format
}

const gettimecodeformatted = ([ssnum, format, opts]) => {
  let timeformat = format || get_best_format([ssnum, opts]),
      unnan = num => Number.isNaN(num) ? 0 : num,
      numpad = num => (String(num).length > 1 ? '' : '0') + num,
      hh,
      mm,
      ss

  hh = Math.floor(ssnum / 3600)
  ssnum -= hh * 3600
  mm = unnan(Math.floor(ssnum / 60))
  ssnum -= mm * 60
  ss = unnan(Math.floor(ssnum))

  return timeformat
    .replace(/hh/, numpad(hh))
    .replace(/h/, hh)
    .replace(/mm/, numpad(mm))
    .replace(/m/, mm)
    .replace(/ss/, numpad(ss))
    .replace('00:', '')
    .replace('00:', '')
}

// const getndscenelabel = ([{ start, type, uuid }], ss, cfg) => (
const getndscenelabel = ([{ start, type, uuid }]) => (
  ':time-:type-:uuid'
    .replace(/:time/,
      gettimecodeformatted([ start < 0 ? 0 : start, 'hh-mm-ss' ]))
    .replace(/:type/, type)
    .replace(/:uuid/, uuid))

const getseqrefdata = (ss, cfg, gr, ndscene, refuuid) => (
  accBaseElem.propget(ndscene, 'shapedict', {})[refuuid])

const addseqrefdatashape = async (ss, cfg, gr, ndscene, pg, seq) => {
  const seqrefdata = getseqrefdata(ss, cfg, gr, ndscene, seq.uuid)
  const ndshapesdata = gr.get(DATASHAPESKEY)
  const ndshapesdataui = nodeosxGet(cfg, gr, ndshapesdata)

  const res = await ndshapesdataui.addchilddata(
    ss, cfg, gr, ndshapesdata, pg, seqrefdata)
  gr = res[0]

  return [gr, ndscene]
}

const addseqrefdata = async (ss, cfg, gr, ndscene, pg, seq) => {
  if (!/shape/.test(seq.type)) {
    console.error('invalid seqtype', gr.toJS(), ndscene.toJS(), seq)
  } else if (seq.type === 'shape') {
    return addseqrefdatashape(ss, cfg, gr, ndscene, pg, seq)
  }
}

// hook called by gani_node
const xhydratedbase = async (ss, cfg, gr, nd, pg) => {
  const sequences = accBaseElem.propget(nd, 'sequences', [])

  return getseqaugmented(ss, cfg, gr, nd, pg, sequences)
}

const getseqshapekeyarr = async (ss, cfg, gr, ndscene) => {
  const ndbgn = getscenemarkerbgnnode(gr, ndscene)
  const ndend = getscenemarkerendnode(gr, ndscene)
  const keyarr = []

  return new Promise((resolve, error) => {
    gngraph_followedge(gr, ndbgn, 'nextseq', err => {
      if (err) {
        error(err)
      } else {
        resolve(keyarr)
      }
    }, (gr, edgenode, edgeout, fn) => {
      const edgekey = edgenode.get(KEY)

      if (!nodeIsSame(edgenode, ndend) &&
        /-shape[^-]*-/gi.test(edgekey)
      ) {
        keyarr.push(edgekey)
      }

      fn(null, gr, edgenode)
    })
  })
}

export default Object.assign({}, ptrecls, {
  type: 'datascene',
  getndscenelabel,
  getseqshapekeyarr,
  xinit,
  xhydratedbase
})
